import { FormControl, FormHelperText, InputLabel, OutlinedInput } from "@mui/material";
import { forwardRef } from "react";
import { Controller, UseFormReturn } from "react-hook-form";
import { IMaskInput } from "react-imask";
import { fieldValueToString } from "../../../utils/react_hook_form_input_utils";

/**
 * FormPercentageMaskedInputNegativoBase
 */

interface CustomProps {
  onChange: (event: { target: { name: string; value: string } }) => void;
  name: string;
  mask: string;
}

const FormPercentageMaskedInputNegativoBase = forwardRef<HTMLInputElement, CustomProps>((props, refs) => {
  const { onChange, ...other } = props;
  return (
    <IMaskInput
      {...other}
      placeholderChar="_"
      inputRef={refs}
      onAccept={(value: any, input: any) => {
        // Extrair o valor raw (html value) para preservar o sinal negativo
        const rawValue = input.element?.value || value;
        const isNegative = rawValue.includes('-');

        // Extrair apenas os números e a vírgula do unmaskedValue
        const unmaskedValue = input.unmaskedValue || '';

        if (unmaskedValue.length > 0) {
          // Reconstruir o valor sem % mas preservando o negativo
          const numericValue = unmaskedValue.replace(/[^\d,]/g, '');
          const finalValue = isNegative ? `-${numericValue}` : numericValue;
          onChange({ target: { name: props.name, value: finalValue } });
        } else {
          onChange({ target: { name: props.name, value: '' } });
        }
      }}
      lazy={false}
      overwrite={true}
      unmask={false}
    />
  );
});

/**
 * FormPercentageNegativeMaskedInput
 */

type Props<T extends {}> = {
  name: keyof T,
  label: string,
  required?: boolean,
  reactHookForm: UseFormReturn<T>,
  readOnly?: boolean,
};

export const FormPercentageNegativeMaskedInput = <T extends {},>({
  name,
  label,
  required,
  reactHookForm,
  readOnly = false,
}: Props<T>) => {
  return (
    <Controller
      name={name as any}
      control={reactHookForm.control}
      render={({ field, formState }) => {
        let helperText: string | null = null;

        if (name in formState.errors) {
          const error = formState.errors[name] as any;
          if (typeof error.message === 'string') {
            helperText = error.message;
          }
        }

        return (
          <FormControl variant="outlined" size="small">
            <InputLabel
              htmlFor={`input-${name as string}`}
              error={helperText !== null}
              shrink={true}
              size="small"
              required={required}>
              {label}
            </InputLabel>

            <OutlinedInput
              {...field}
              size="small"
              id={`input-${name as string}`}
              name={name as string}
              label={label}
              notched={true}
              required={required}
              readOnly={readOnly}
              disabled={formState.disabled}
              aria-describedby={`${name as string}-component-error-text`}
              inputComponent={FormPercentageMaskedInputNegativoBase as any}
              value={fieldValueToString(field.value)}
              inputProps={{
                mask: [
                  {
                    mask: [
                      { mask: '\\0,\\0\\0 %', lazy: true },
                      { mask: '\\0,\\00 %', lazy: false },
                      { mask: '\\0,00 %', lazy: false },
                      { mask: '0,00 %', lazy: false },
                      { mask: '00,00 %', lazy: false },
                      { mask: '000,00 %', lazy: false },
                    ],
                    isNegative: false,
                  },
                  {
                    mask: [
                      { mask: '-\\0,\\0\\0 %', lazy: true },
                      { mask: '-\\0,\\00 %', lazy: false },
                      { mask: '-\\0,00 %', lazy: false },
                      { mask: '-0,00 %', lazy: false },
                      { mask: '-00,00 %', lazy: false },
                      { mask: '-000,00 %', lazy: false },
                    ],
                    isNegative: true,
                  },
                ],
                dispatch: (appended: string, dynamicMasked: any) => {
                  const value = (dynamicMasked.value + appended).trim();
                  const isNegative = value.startsWith('-');
                  return dynamicMasked.compiledMasks.find(
                    (mask: any) => mask.isNegative === isNegative
                  );
                },
                placeholder: "___,__ %",
                required: required,
              }} />

            <FormHelperText id={`${name as string}-component-error-text`} error={true}>
              {helperText}
            </FormHelperText>
          </FormControl>
        );
      }} />
  );
}